package one.healthium.log_food.ui.find_food

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyItemScope
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.material3.Icon
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.platform.LocalSoftwareKeyboardController
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import ifaandroid.feature.profile.log_food.generated.resources.Res
import ifaandroid.feature.profile.log_food.generated.resources.ic_barcode_24
import kotlinx.coroutines.launch
import loco.LOG_FOOD_FIND_FOOD_EMPTY_MESSAGE
import loco.LOG_FOOD_FIND_FOOD_EMPTY_TITLE
import loco.LOG_FOOD_FIND_FOOD_SCAN_BARCODE_BUTTON
import loco.LOG_FOOD_SCAN_FOOD_BUTTON
import one.healthium.base.preferences.AppPreferences
import one.healthium.base.utils.Formatters.formatCalories
import one.healthium.base.utils.Formatters.formatGrams
import one.healthium.base.utils.Formatters.formatOz
import one.healthium.loco.resolve
import one.healthium.log_food.ui.find_food.components.SearchTextField
import one.healthium.profile.ui.food.models.FoodUiModel
import one.healthium.scanner.ScannerPermissionRequester
import one.healthium.ui.common.event_producer.EventEffect
import one.healthium.ui.compose.components.AppIcons
import one.healthium.ui.compose.components.AppLoadingScreen
import one.healthium.ui.compose.theme.AppTheme
import one.healthium.ui.compose.theme.gray_13
import one.healthium.ui.compose.theme.gray_15
import one.healthium.ui.compose.theme.gray_3
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.koinInject
import org.koin.compose.viewmodel.koinViewModel

interface FindFoodEventDelegate {
    fun onFoodClicked(food: FoodUiModel)
    suspend fun onScanBarcode(): String?
    suspend fun onScanFood(): List<FoodUiModel>?
    fun onFoodsScanned(foods: List<FoodUiModel>)
}

@Composable
fun FindFoodScreen(
    eventDelegate: FindFoodEventDelegate,
) {
    val viewModel = koinViewModel<FindFoodViewModel>()
    val state by viewModel.state.collectAsState()
    val scope = rememberCoroutineScope()

    EventEffect(viewModel.events) {
        when (it) {
            is FindFoodEvents.FoodSelected -> eventDelegate.onFoodClicked(it.food)
        }
    }

    FindFoodContent(
        state = state,
        onQueryChanged = viewModel::onQueryChanged,
        onPerformSearch = viewModel::onPerformSearch,
        onFoodClicked = eventDelegate::onFoodClicked,
        onScanBarcodeClicked = {
            scope.launch {
                val barcode = eventDelegate.onScanBarcode()
                viewModel.onBarcodeScanned(barcode)
            }
        },
        onScanFoodClicked = {
            if (state.isScannerEnabled) {
                scope.launch {
                    val foods = eventDelegate.onScanFood()
                    if (!foods.isNullOrEmpty()) {
                        eventDelegate.onFoodsScanned(foods)
                    }
                }
            }
        },
    )
}

@Composable
internal fun FindFoodContent(
    state: FindFoodUiState,
    onQueryChanged: (String) -> Unit,
    onPerformSearch: () -> Unit,
    onFoodClicked: (FoodUiModel) -> Unit,
    onScanBarcodeClicked: () -> Unit,
    onScanFoodClicked: () -> Unit,
) {
    Column(
        modifier = Modifier.fillMaxSize()
    ) {
        FindFoodList(
            modifier = Modifier.weight(1f),
            foods = state.uiData,
            loading = state.loading,
            onFoodClicked = onFoodClicked,
        )
        if (state.isScannerEnabled) {
            ScanFoodButton(onClick = onScanFoodClicked)
//            ScanBarcodeButton(onClick = onScanBarcodeClicked)
        }
        FindFoodSearchField(
            searchQuery = state.searchQuery,
            onQueryChanged = onQueryChanged,
            onPerformSearch = onPerformSearch,
        )
    }
}

@Composable
private fun FindFoodList(
    modifier: Modifier = Modifier,
    foods: List<Any>?,
    loading: Boolean,
    onFoodClicked: (FoodUiModel) -> Unit,
) {
    Box(modifier) {
        if (foods != null) {
            val keyboardController = LocalSoftwareKeyboardController.current
            val listState = rememberLazyListState()

            LaunchedEffect(listState.isScrollInProgress) {
                if (listState.isScrollInProgress) {
                    keyboardController?.hide()
                }
            }

            LazyColumn(
                modifier = Modifier.fillMaxSize().testTag("food_results"),
                state = listState,
                contentPadding = PaddingValues(horizontal = 12.dp, vertical = 24.dp),
            ) {
                items(
                    count = foods.size,
                    contentType = { index -> foods[index]::class }
                ) { index ->
                    if (index > 0) {
                        Spacer(Modifier.height(6.dp))
                    }
                    when (val uiModel = foods[index]) {
                        is FoodUiModel -> FoodItem(
                            uiModel = uiModel,
                            onClick = onFoodClicked,
                        )

                        is String -> Text(
                            modifier = Modifier.padding(vertical = 6.dp),
                            text = uiModel,
                            style = AppTheme.textStyles.secondary_14_400,
                            color = gray_3,
                        )

                        is EmptyFindUiModel -> EmptyStateItem()
                    }
                }
            }
        }
        AppLoadingScreen(
            modifier = Modifier.testTag("find_food_loading"),
            visible = loading,
            dim = foods?.isNotEmpty() == true,
            isDark = false,
        )
    }
}

@Composable
private fun FoodItem(
    uiModel: FoodUiModel,
    onClick: (FoodUiModel) -> Unit,
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .background(gray_13, AppTheme.shapes.small)
            .clip(AppTheme.shapes.small)
            .clickable { onClick(uiModel) }
            .padding(horizontal = 12.dp, vertical = 16.dp),
    ) {
        Text(
            modifier = Modifier.padding(start = 4.dp, top = 4.dp),
            text = uiModel.emoji,
            fontSize = 24.sp,
        )
        Spacer(Modifier.width(16.dp))
        Column(
            modifier = Modifier.weight(1f)
        ) {
            Text(
                text = uiModel.product,
                style = AppTheme.textStyles.secondary_16_500,
                color = AppTheme.colors.text100,
                maxLines = 2,
            )
            Text(
                text = uiModel.description,
                style = AppTheme.textStyles.secondary_14_400,
                color = gray_3,
                maxLines = 2,
            )
            Text(
                text = rememberCaloriesPerUnitDisplay(uiModel),
                style = AppTheme.textStyles.secondary_14_400,
                color = AppTheme.colors.text100,
                maxLines = 1,
            )
        }
        Spacer(Modifier.width(10.dp))
        Image(
            modifier = Modifier.align(Alignment.CenterVertically),
            painter = painterResource(AppIcons.icArrowRight),
            contentDescription = null,
        )
    }
}

@Composable
private fun LazyItemScope.EmptyStateItem() {
    Column(
        modifier = Modifier.fillParentMaxHeight().fillMaxWidth(),
        verticalArrangement = Arrangement.Center,
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Text(
            text = "🧐",
            style = AppTheme.textStyles.secondary_32_600,
        )
        Text(
            text = stringResource(loco.Res.string.LOG_FOOD_FIND_FOOD_EMPTY_TITLE),
            style = AppTheme.textStyles.secondary_17_600,
            color = AppTheme.colors.text100,
            textAlign = TextAlign.Center,
        )
        Spacer(Modifier.height(12.dp))
        Text(
            text = stringResource(loco.Res.string.LOG_FOOD_FIND_FOOD_EMPTY_MESSAGE),
            style = AppTheme.textStyles.secondary_14_400,
            color = gray_3,
            textAlign = TextAlign.Center,
        )
        Spacer(Modifier.height(12.dp))
    }
}

@Composable
private fun ScanFoodButton(onClick: () -> Unit) {
    val keyboardController = LocalSoftwareKeyboardController.current

    ScannerPermissionRequester { request ->
        val scope = rememberCoroutineScope()
        Surface(
            color = AppTheme.colors.primary,
            contentColor = AppTheme.colors.onPrimary,
            onClick = {
                keyboardController?.hide()
                scope.launch {
                    val granted = request()
                    if (granted) {
                        onClick()
                    }
                }
            },
        ) {
            Row(
                modifier = Modifier.fillMaxWidth().padding(12.dp),
                horizontalArrangement = Arrangement.Center,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Icon(
                    painter = painterResource(Res.drawable.ic_barcode_24),
                    contentDescription = null,
                )
                Spacer(Modifier.width(6.dp))
                Text(
                    text = stringResource(loco.Res.string.LOG_FOOD_SCAN_FOOD_BUTTON),
                    style = AppTheme.textStyles.secondary_16_500,
                )
            }
        }
    }
}

@Composable
private fun ScanBarcodeButton(onClick: () -> Unit) {
    val keyboardController = LocalSoftwareKeyboardController.current
    val scope = rememberCoroutineScope()

    ScannerPermissionRequester { request ->
        Surface(
            color = AppTheme.colors.primary,
            contentColor = AppTheme.colors.onPrimary,
            onClick = {
                keyboardController?.hide()
                scope.launch {
                    val granted = request()
                    if (granted) {
                        onClick()
                    }
                }
            },
        ) {
            Row(
                modifier = Modifier.fillMaxWidth().padding(12.dp),
                horizontalArrangement = Arrangement.Center,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Icon(
                    painter = painterResource(Res.drawable.ic_barcode_24),
                    contentDescription = null,
                )
                Spacer(Modifier.width(6.dp))
                Text(
                    text = stringResource(loco.Res.string.LOG_FOOD_FIND_FOOD_SCAN_BARCODE_BUTTON),
                    style = AppTheme.textStyles.secondary_16_500,
                )
            }
        }
    }
}

@Composable
private fun FindFoodSearchField(
    searchQuery: String,
    onQueryChanged: (String) -> Unit,
    onPerformSearch: () -> Unit,
) {
    val keyboardController = LocalSoftwareKeyboardController.current

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .background(gray_15)
            .padding(horizontal = 12.dp, vertical = 6.dp)
            .navigationBarsPadding()
    ) {
        SearchTextField(
            value = searchQuery,
            onValueChange = onQueryChanged,
            keyboardActions = KeyboardActions {
                keyboardController?.hide()
                onPerformSearch()
            },
            showKeyboardAutomaticallyIfEmpty = false,
        )
    }
}

@Composable
@Preview
private fun FindFoodContentPreview() {
    AppTheme {
        FindFoodContent(
            state = FindFoodUiState(
                searchQuery = "Steak",
                foodSuggestions = listOf(
                    FoodUiModel(
                        product = "Steak",
                        emoji = "🥩",
                        description = "Beef steak",
                        calories = 100f,
                        fat = 10f,
                        carbohydrates = 10f,
                        fiber = 10f,
                        protein = 10f,
                        tablespoon = 16f,
                        cups = 4f,
                        pieces = 100f,
                        portion = 1f,
                        isLiquid = false,
                    ),
                ),
            ),
            onQueryChanged = {},
            onPerformSearch = {},
            onFoodClicked = {},
            onScanBarcodeClicked = {},
            onScanFoodClicked = {},
        )
    }
}

@Composable
private fun rememberCaloriesPerUnitDisplay(uiModel: FoodUiModel): String {
    val appPreferences = koinInject<AppPreferences>()
    val isMetric by appPreferences.isMetricFlow.collectAsState()
    return if (isMetric) {
        val caloriesPart = uiModel.caloriesPerHundredGrams.formatCalories().resolve()
        val unitPart = 100f.formatGrams().resolve()
        "$caloriesPart / $unitPart"
    } else {
        val ozCalories = uiModel.caloriesPerOz
        val caloriesPart = ozCalories.formatCalories().resolve()
        val unitPart = 1f.formatOz().resolve()
        "$caloriesPart / $unitPart"
    }
}
